import React, { PureComponent } from 'react'
import prop from 'lodash/fp/prop'
import noop from 'lodash/noop'
import Button from '@active/react-ui/Button'
import Checkbox from '@active/react-ui/Checkbox'
import Input from '@active/react-ui/Input'
import LinkButton from '@active/react-ui/LinkButton'
import {
  string, func, bool,
} from 'prop-types'
import Identity from '@active/react-utils/components/Identity'
import { withI18n } from '@active/react-utils/i18n'
import I18nMessage from '../I18nMessage'
import './MFAContent.scss'

const MASKER = '******'
const VERIFY = 'VERIFY'
const SMS_CHANNEL = 'SMS'
const EMAIL_CHANNEL = 'EMAIL'
const RESEND = 'RESEND'

export const calculateCounter = dateStr => {
  const subtractor = new Date()
  const minuened = new Date(dateStr)
  if (subtractor >= minuened) {
    return 0
  }

  return Math.ceil((minuened - subtractor) / 1000)
}

class MFAContent extends PureComponent {
  static propTypes = {
    onClose: func,
    onRequestCode: func.isRequired,
    configureTrack: func.isRequired,
    onVerify: func.isRequired,
    senderEmail: string.isRequired,
    senderPhone: string,
    i18n: func.isRequired,
    nextTimeWindow: string.isRequired,
    isShowCancelBtn: bool,
    message: string, // for page
    channel: string, // for page
    renderModel: string, // for page render
    epid: string,
    Body: func,
    Footer: func,
  }

  static defaultProps = {
    isShowCancelBtn: true,
    onClose: noop,
    senderPhone: undefined,
    message: undefined,
    channel: undefined,
    renderModel: undefined,
    epid: '',
    Body: Identity,
    Footer: Identity,
  }

  interval = null

  constructor(props) {
    super(props)
    const {
      nextTimeWindow, message, channel,
    } = props
    this.state = {
      counter: calculateCounter(nextTimeWindow),
      code: '',
      errorText: message || '',
      hasAuthorized: true,
      isSMSchannel: channel === SMS_CHANNEL,
    }
    this.maskSenderEmail = this.maskSenderEmail.bind(this)
  }

  componentDidMount() {
    this.interval = setInterval(() => {
      this.updateCounter()
    }, 1000)
    const {
      epid, configureTrack, channel, renderModel,
    } = this.props
    configureTrack({
      version: 'v5',
      pageType: 'sign in|MFA',
      mfaChannel: channel,
      widget: renderModel === 'PAGE_RENDER' ? '' : 'signIn_MFA',
      eventTracking: 'init',
      epid: epid,
    })
  }

  componentWillUnmount() {
    this.clearTimer()
  }

  onCodeChange = event => {
    this.setState({
      code: prop('target.value')(event),
    })
  }

  onResendButtonClick = async () => {
    const { isSMSchannel } = this.state
    const { onRequestCode } = this.props
    this.setState({
      errorText: '',
      code: '',
    })
    const result = await onRequestCode({
      operation: RESEND,
      channel: isSMSchannel ? SMS_CHANNEL : EMAIL_CHANNEL,
    })
    const { message } = result
    this.setState({ errorText: message || '' })
  }

  onVerifyButtonClick = async () => {
    const {
      epid, onVerify, configureTrack, renderModel,
    } = this.props
    const { code, hasAuthorized, isSMSchannel } = this.state
    configureTrack({
      version: 'v5',
      pageType: 'sign in|MFA',
      mfaChannel: isSMSchannel ? SMS_CHANNEL : EMAIL_CHANNEL,
      widget: renderModel === 'PAGE_RENDER' ? '' : 'signIn_MFA',
      eventTracking: 'verify',
      epid: epid,
    })
    const result = await onVerify({
      otp: code,
      hasAuthorized,
      operation: VERIFY,
      channel: isSMSchannel ? SMS_CHANNEL : EMAIL_CHANNEL,
    })
    const { message } = result
    this.setState({ errorText: message || '' })
  }

  onToggleHasAuthorizedStatus = () => {
    this.setState(({ hasAuthorized }) => ({
      hasAuthorized: !hasAuthorized,
    }))
  }

  maskSenderEmail = () => {
    const { senderEmail } = this.props
    const [toBeMask, suffix] = senderEmail.split('@')
    const masked = `${toBeMask.substring(0, 2)}${MASKER}`
    return [masked, suffix].join('@')
  }

  maskSenderPhone = () => {
    const { senderPhone } = this.props
    return `${senderPhone.substring(0, 3)}${MASKER}${senderPhone.substring(senderPhone.length - 3, senderPhone.length)}`
  }

  handleSwitchChannel = () => {
    const { isSMSchannel } = this.state
    this.setState({ isSMSchannel: !isSMSchannel }, () => {
      this.onResendButtonClick()
    })
  }

  updateCounter() {
    const { nextTimeWindow } = this.props
    this.setState({
      counter: calculateCounter(nextTimeWindow),
    })
  }

  clearTimer() {
    this.interval && clearInterval(this.interval)
  }

  render() {
    const {
      onClose, i18n, senderPhone, isShowCancelBtn, Body, Footer,
    } = this.props
    const {
      counter, errorText, code, hasAuthorized, isSMSchannel,
    } = this.state
    const description = isSMSchannel ? 'passport.verification.modal.description.sendSMSTo' : 'passport.verification.modal.descritpion.sendEmailTo'
    const descritpionValues = { 0: isSMSchannel ? this.maskSenderPhone() : this.maskSenderEmail() }
    const switchDescription = isSMSchannel ? 'passport.verification.modal.description.verifyByEmail' : 'passport.verification.modal.description.verifyByPhone'

    return (
      <Identity>
        <Body>
          <div className="passport-content mfa-content">
            <div className="mfa-content__description">
              <I18nMessage id={description} params={descritpionValues} />
            </div>
            <div className="mfa-content__toolTip">
              <I18nMessage id="passport.verification.modal.toolTip" />
            </div>
            <div className="mfa-content__verify-section">
              <div className="mfa-content__input-container">
                <Input
                  placeholder={i18n('passport.verification.modal.input.placeholder')}
                  errored={`${!!errorText}`}
                  onChange={this.onCodeChange}
                  value={code}
                />
                {!!errorText && (
                  <div className="form-group-error">
                    <i className="ic-cross-r" />
                    <span aria-live="assertive">{errorText}</span>
                  </div>
                )}
              </div>
              <Button
                type="primary"
                className="mfa-content__button--resend"
                disabled={counter > 0}
                onClick={this.onResendButtonClick}
              >
                {counter > 0 ? `${counter}s` : <I18nMessage id="passport.common.btn.send" />}
              </Button>
            </div>
            <Checkbox
              className="mfa-content__has-authorized"
              checked={hasAuthorized}
              onChange={this.onToggleHasAuthorizedStatus}
              name="hasAuthorized"
              aria-checked={hasAuthorized}
            >
              <I18nMessage id="passport.trust.device.label.authorized" />
            </Checkbox>
          </div>
        </Body>

        <Footer>
          <div className="passport-content-footer">
            <div className="mfa-content__buttons">
              <Button type="super" disabled={!code} className="mfa-content__button--send" onClick={this.onVerifyButtonClick}>
                <I18nMessage id="passport.verification.modal.button.verify.text" />
              </Button>
              {isShowCancelBtn && (
                <Button type="secondary" onClick={onClose}>
                  <I18nMessage id="passport.verification.modal.button.cancel.text" />
                </Button>
              )}
            </div>
            {!!senderPhone && counter === 0 && (
              <LinkButton className="mfa-content__link" onClick={this.handleSwitchChannel}>
                <I18nMessage id={switchDescription} />
              </LinkButton>
            )}
          </div>
        </Footer>
      </Identity>
    )
  }
}

export default withI18n(MFAContent)
